import React, { useEffect, useRef } from 'react';
import _get from 'lodash/get';
import { Box, Button, Paper, Typography, makeStyles } from '@material-ui/core';
import Mixpanel from 'utils/mixpanelService';
import Carousel from 'components/ResourcesCarousel';
import RichTextReactRenderer from 'components/RichTextReactRenderer';
import ResourceItemSelector from 'components/ResourceItemSelector';
import { courseSharedStyles } from '../courseSharedStyles';

const useStyles = makeStyles(theme => ({
  root: {
    '& > div + div': {
      marginTop: 20,
    },
  },
  richText: {
    '&  p, & span[role="button"]': {
      fontSize: props => props.fontSize,
    },
  },
  video: {
    width: '100%',
  },
  recommendedCarousel: {
    position: 'relative',
    width: '100%',

    '& .cm-resources-carousel-item': {
      padding: 0,
      '&:not(:first-child)': {
        paddingLeft: 12,
      },
    },
  },
  completeButton: {
    marginLeft: props => !props.small && theme.spacing(2),
    marginBottom: props => props.small && theme.spacing(2),
    padding: '10px 16px',
    borderRadius: 20,
    minWidth: 150,
    alignSelf: 'flex-start',
    border: '1px solid transparent',
    '&:disabled,&:disabled:hover': {
      pointerEvents: 'none',
      backgroundColor: '#E0E0E0',
    },
  },
  ...courseSharedStyles(),
}));

export const CourseNextSteps = ({
  course,
  series,
  seriesSiteCopy,
  coursesSiteCopy,
  fontSize,
  onSeriesComplete,
  isModuleCompleted,
  onNext,
  activeModule,
  trackingPath,
  small,
  isSeriesCompleted,
  isCourseCompleted,
  isLastModule,
}) => {
  const isCourseCompleteRef = useRef(isCourseCompleted);
  const ref = useRef(null);

  const classes = useStyles({
    fontSize,
    small,
  });

  // Automatically complete next steps if it's not completed
  useEffect(() => {
    if (isSeriesCompleted) return;

    onSeriesComplete(false, false, true);

    Mixpanel.track('Course Module - Completed', {
      course: course.fields.title,
      module: activeModule.fields.title,
      path: trackingPath,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSeriesCompleted]);

  const handleNext = () => {
    onNext();
  };

  const readMoreLessHandler = shortMode => {
    if (!shortMode && ref) {
      // update scroll in the next frame once rich shrinks
      requestAnimationFrame(() => {
        ref.current.scrollIntoView({ block: 'end' });
      });
    }
  };

  const video = _get(series, 'fields.assets', []).find(asset =>
    _get(asset, 'fields.file.contentType').includes('video'),
  );

  const buttonTextProperty = !isLastModule
    ? 'nextModule'
    : isCourseCompleteRef.current
    ? 'goToDashboard'
    : 'completeCourse';

  const completeButton = isModuleCompleted && (
    <Button
      className={classes.completeButton}
      variant="contained"
      color="primary"
      onClick={handleNext}
    >
      {_get(coursesSiteCopy, `pageCopy.${buttonTextProperty}`)}
    </Button>
  );

  return (
    <section className={classes.root}>
      {small && completeButton}
      <Box ref={ref} display="flex" justifyContent="space-between">
        <RichTextReactRenderer
          className={classes.richText}
          data={_get(series.fields, 'learningResultsSummary')}
          limit={450}
          readMoreLessHandler={readMoreLessHandler}
        />
        {!small && completeButton}
      </Box>
      <Paper elevation={0} className={classes.videoPaper}>
        {video && (
          <video className={classes.video} controls>
            <source
              src={video.fields.file.url}
              type={video.fields.file.contentType}
            />
          </video>
        )}
      </Paper>
      {_get(series.fields, 'recommendedResources') && (
        <div>
          <Typography color="textPrimary" variant="h6" gutterBottom>
            {_get(seriesSiteCopy, 'pageCopy.recommendedResourcesTitle')}
          </Typography>
          <div className={classes.recommendedCarousel}>
            <Carousel
              small
              slidesToSlide={small ? 1 : 3}
              swipeable
              responsive={{
                desktop: {
                  breakpoint: {
                    max: 4000,
                    min: 1024,
                  },
                  items: 4,
                },
                bigTablet: {
                  breakpoint: {
                    max: 1200,
                    min: 960,
                  },
                  items: 3,
                },
                tablet: {
                  breakpoint: {
                    max: 960,
                    min: 600,
                  },
                  items: 2,
                },
                mobile: {
                  breakpoint: {
                    max: 600,
                    min: 0,
                  },
                  items: 1,
                },
              }}
            >
              {series.fields.recommendedResources.map(el => (
                <ResourceItemSelector
                  item={el}
                  key={el.sys.id}
                  trackingPath={trackingPath}
                />
              ))}
            </Carousel>
          </div>
        </div>
      )}
    </section>
  );
};
